import { useState, useCallback, useRef, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { translations } from "./translations";
import { 
  FilePdf, 
  Upload, 
  Trash, 
  Download, 
  Copy, 
  Check,
  GraduationCap,
  Briefcase,
  ListBullets,
  Article,
  Clock,
  Globe,
  SpinnerGap,
  SignOut,
  GoogleLogo
} from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";
import { ScrollArea } from "./components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Summary type icons mapping
const typeIcons = {
  exam: GraduationCap,
  executive: Briefcase,
  key_points: ListBullets,
  detailed: Article
};

// Summary type colors mapping
const typeColors = {
  exam: "neo-badge-mint",
  executive: "neo-badge-lavender",
  key_points: "neo-badge-pink",
  detailed: "neo-badge-mint"
};

function App() {
  const [language, setLanguage] = useState("pt");
  const [file, setFile] = useState(null);
  const [summaryType, setSummaryType] = useState("key_points");
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState("");
  const [currentSummary, setCurrentSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [copied, setCopied] = useState(false);
  const [usage, setUsage] = useState({ used: 0, limit: 3, remaining: 3, max_pages: 10, authenticated: false });
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef(null);
  
  const t = translations[language];

  // Check for session_token in URL (after Google OAuth redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionToken = urlParams.get("session_token");
    const error = urlParams.get("error");
    
    if (error) {
      toast.error("Login error: " + error);
      window.history.replaceState({}, document.title, window.location.pathname);
      setIsLoading(false);
      return;
    }
    
    if (sessionToken) {
      // Store session token and check auth
      document.cookie = `session_token=${sessionToken}; path=/; max-age=${7*24*60*60}; SameSite=None; Secure`;
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Check if already logged in
    const checkAuthStatus = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        // Fetch usage and history after successful auth
        const usageRes = await axios.get(`${API}/usage`, { withCredentials: true });
        setUsage(usageRes.data);
        const historyRes = await axios.get(`${API}/summaries`, { withCredentials: true });
        setHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
      } catch (err) {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuthStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      fetchUsage();
      fetchHistory();
    } catch (err) {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${API}/auth/google`;
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      setHistory([]);
      setCurrentSummary(null);
      setUsage({ used: 0, limit: 3, remaining: 0, max_pages: 10, authenticated: false });
      toast.success(language === "pt" ? "Logout realizado!" : "Logged out!");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/usage`, { withCredentials: true });
      setUsage(response.data);
    } catch (error) {
      console.error("Error fetching usage:", error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/summaries`, { withCredentials: true });
      setHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching history:", error);
      setHistory([]);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
    } else {
      toast.error(t.selectFile);
    }
  }, [t.selectFile]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
    } else {
      toast.error(t.selectFile);
    }
  };

  const handleSummarize = async () => {
    if (!file) {
      toast.error(t.selectFile);
      return;
    }

    if (!user) {
      toast.error(language === "pt" 
        ? "Faça login com Google para usar o ResumoPDF" 
        : "Please login with Google to use ResumoPDF");
      return;
    }

    if (usage.remaining <= 0) {
      toast.error(language === "pt" 
        ? "Limite gratuito atingido. Volte amanhã ou faça upgrade." 
        : "Free limit reached. Come back tomorrow or upgrade.");
      return;
    }

    setIsProcessing(true);
    setProcessingStep(t.extractingText);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("summary_type", summaryType);
    formData.append("language", language);

    try {
      setProcessingStep(t.analyzingContent);
      
      setTimeout(() => {
        setProcessingStep(t.generatingSummary);
      }, 2000);

      const response = await axios.post(`${API}/summarize`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true
      });

      setCurrentSummary(response.data);
      setHistory(prev => [response.data, ...prev]);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      
      await fetchUsage();
      
      toast.success(language === "pt" ? "Resumo gerado!" : "Summary generated!");
    } catch (error) {
      console.error("Error:", error);
      
      const errorData = error.response?.data?.detail;
      if (errorData?.error === "limit_reached") {
        toast.error(language === "pt" ? errorData.message_pt : errorData.message_en);
        await fetchUsage();
      } else if (errorData?.error === "page_limit") {
        toast.error(language === "pt" ? errorData.message_pt : errorData.message_en);
      } else if (errorData?.error === "auth_required") {
        toast.error(language === "pt" ? errorData.message_pt : errorData.message_en);
      } else {
        toast.error(t.errorProcess);
      }
    } finally {
      setIsProcessing(false);
      setProcessingStep("");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/summaries/${id}`, { withCredentials: true });
      setHistory(prev => prev.filter(s => s.id !== id));
      if (currentSummary?.id === id) {
        setCurrentSummary(null);
      }
      toast.success(t.successDelete);
    } catch (error) {
      toast.error(t.errorDelete);
    }
  };

  const handleDownload = async (id, format) => {
    try {
      const response = await axios.get(`${API}/summaries/${id}/download/${format}`, {
        responseType: "blob",
        withCredentials: true
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `summary_${id}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  const handleCopy = async () => {
    if (currentSummary) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(currentSummary.summary_text);
        } else {
          const textArea = document.createElement("textarea");
          textArea.value = currentSummary.summary_text;
          textArea.style.position = "fixed";
          textArea.style.left = "-999999px";
          textArea.style.top = "-999999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand("copy");
          textArea.remove();
        }
        setCopied(true);
        toast.success(t.successCopy);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        const textArea = document.createElement("textarea");
        textArea.value = currentSummary.summary_text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand("copy");
          setCopied(true);
          toast.success(t.successCopy);
          setTimeout(() => setCopied(false), 2000);
        } catch (e) {
          toast.error(language === "pt" ? "Não foi possível copiar" : "Could not copy");
        }
        textArea.remove();
      }
    }
  };

  const selectHistoryItem = (item) => {
    setCurrentSummary(item);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === "pt" ? "pt-BR" : "en-US", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FEF08A" }}>
        <SpinnerGap size={48} weight="bold" className="animate-spin" />
      </div>
    );
  }

  // Login screen if not authenticated
  if (!user) {
    return (
      <div 
        className="min-h-screen flex flex-col items-center justify-center p-4"
        style={{ backgroundColor: "#FEF08A" }}
        data-testid="login-screen"
      >
        <Toaster position="top-right" richColors />
        
        <div className="neo-card p-8 max-w-md w-full text-center">
          <div className="p-3 bg-[#A7F3D0] border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] inline-block mb-4">
            <FilePdf size={48} weight="bold" />
          </div>
          
          <h1 className="heading text-3xl font-bold mb-2">{t.appTitle}</h1>
          <p className="text-gray-600 mb-6">{t.appSubtitle}</p>
          
          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-2 text-left">
              <div className="w-2 h-2 bg-[#A7F3D0] border border-black"></div>
              <span className="text-sm">{language === "pt" ? "3 resumos grátis por dia" : "3 free summaries per day"}</span>
            </div>
            <div className="flex items-center gap-2 text-left">
              <div className="w-2 h-2 bg-[#E9D5FF] border border-black"></div>
              <span className="text-sm">{language === "pt" ? "Máx. 10 páginas por PDF" : "Max. 10 pages per PDF"}</span>
            </div>
            <div className="flex items-center gap-2 text-left">
              <div className="w-2 h-2 bg-[#FBCFE8] border border-black"></div>
              <span className="text-sm">{language === "pt" ? "Histórico salvo na sua conta" : "History saved to your account"}</span>
            </div>
          </div>
          
          <button
            onClick={handleGoogleLogin}
            className="neo-button w-full py-4 text-lg font-bold flex items-center justify-center gap-3 bg-white hover:bg-gray-50"
            data-testid="google-login-btn"
          >
            <GoogleLogo size={24} weight="bold" />
            {language === "pt" ? "Entrar com Google" : "Sign in with Google"}
          </button>
          
          <button
            onClick={() => setLanguage(language === "pt" ? "en" : "pt")}
            className="mt-4 text-sm text-gray-600 hover:text-black flex items-center justify-center gap-1 mx-auto"
          >
            <Globe size={16} weight="bold" />
            {language === "pt" ? "English" : "Português"}
          </button>
        </div>
        
        <p className="mt-8 text-sm text-gray-600">{t.poweredBy}</p>
      </div>
    );
  }

  // Main app (authenticated)
  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: "#FEF08A" }}
      data-testid="app-container"
    >
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="border-b-2 border-black bg-white p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#A7F3D0] border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <FilePdf size={28} weight="bold" />
            </div>
            <div>
              <h1 className="heading text-2xl font-bold tracking-tight" data-testid="app-title">
                {t.appTitle}
              </h1>
              <p className="text-sm text-gray-600">{t.appSubtitle}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Usage Counter */}
            <div 
              className={`neo-badge px-3 py-1.5 ${usage.remaining > 0 ? 'neo-badge-mint' : 'neo-badge-pink'}`}
              data-testid="usage-counter"
            >
              <span className="text-xs font-bold">
                {usage.remaining}/{usage.limit} {language === "pt" ? "restantes" : "remaining"}
              </span>
            </div>
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="neo-button p-1 flex items-center gap-2" data-testid="user-menu">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border-2 border-black" />
                  ) : (
                    <div className="w-8 h-8 rounded-full border-2 border-black bg-[#E9D5FF] flex items-center justify-center">
                      {user.name?.charAt(0) || "U"}
                    </div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="border-2 border-black bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <div className="px-3 py-2 border-b border-gray-200">
                  <p className="font-semibold text-sm">{user.name}</p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
                <DropdownMenuItem 
                  onClick={() => setLanguage(language === "pt" ? "en" : "pt")}
                  className="cursor-pointer font-medium"
                >
                  <Globe size={16} className="mr-2" />
                  {language === "pt" ? "English" : "Português"}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="cursor-pointer font-medium text-red-600"
                  data-testid="logout-btn"
                >
                  <SignOut size={16} className="mr-2" />
                  {language === "pt" ? "Sair" : "Logout"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          
          {/* Sidebar - History */}
          <aside className="lg:col-span-1 order-2 lg:order-1">
            <div className="neo-card p-4" data-testid="history-panel">
              <h2 className="heading text-lg font-bold mb-4 flex items-center gap-2">
                <Clock size={20} weight="bold" />
                {t.historyTitle}
              </h2>
              
              <ScrollArea className="h-[400px] lg:h-[600px]">
                {history.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-8">
                    {t.noHistory}
                  </p>
                ) : (
                  <div className="space-y-2 pr-2">
                    {history.map((item) => {
                      const TypeIcon = typeIcons[item.summary_type] || ListBullets;
                      return (
                        <div
                          key={item.id}
                          onClick={() => selectHistoryItem(item)}
                          className={`history-item p-3 cursor-pointer ${
                            currentSummary?.id === item.id ? "active" : ""
                          }`}
                          data-testid={`history-item-${item.id}`}
                        >
                          <div className="flex items-start gap-2">
                            <TypeIcon size={18} weight="bold" className="mt-0.5 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate">
                                {item.filename}
                              </p>
                              <p className="text-xs text-gray-600 mt-1">
                                {formatDate(item.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </div>
          </aside>

          {/* Main Workspace */}
          <div className="lg:col-span-3 space-y-6 order-1 lg:order-2">
            
            {/* Upload Zone */}
            <div className="neo-card p-6" data-testid="upload-section">
              <div
                className={`upload-zone p-8 flex flex-col items-center justify-center min-h-[200px] ${
                  isDragging ? "dragging" : ""
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone"
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf"
                  className="hidden"
                  data-testid="file-input"
                />
                
                {file ? (
                  <div className="text-center">
                    <FilePdf size={48} weight="bold" className="mx-auto mb-3 text-black" />
                    <p className="font-semibold text-lg">{file.name}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload size={48} weight="bold" className="mx-auto mb-3 text-gray-400" />
                    <p className="font-semibold text-lg">{t.uploadTitle}</p>
                    <p className="text-sm text-gray-600 mt-1">{t.uploadSubtitle}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      {language === "pt" 
                        ? `Máx. ${usage.max_pages} páginas por PDF` 
                        : `Max. ${usage.max_pages} pages per PDF`}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Summary Type Selection */}
            <div className="neo-card p-6" data-testid="summary-type-section">
              <h3 className="heading text-lg font-bold mb-4 label-uppercase">
                {t.summaryTypeTitle}
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(t.types).map(([key, value]) => {
                  const TypeIcon = typeIcons[key];
                  return (
                    <div
                      key={key}
                      onClick={() => setSummaryType(key)}
                      className={`type-card p-4 ${summaryType === key ? "selected" : ""}`}
                      data-testid={`type-${key}`}
                    >
                      <TypeIcon size={24} weight="bold" className="mb-2" />
                      <p className="font-semibold text-sm">{value.title}</p>
                      <p className="text-xs text-gray-600 mt-1">{value.description}</p>
                    </div>
                  );
                })}
              </div>
              
              {/* Summarize Button */}
              {usage.remaining <= 0 ? (
                <div className="neo-card mt-6 p-4 bg-[#FBCFE8] border-2 border-black text-center">
                  <p className="font-bold text-lg">
                    {language === "pt" 
                      ? "Limite gratuito atingido." 
                      : "Free limit reached."}
                  </p>
                  <p className="text-sm mt-1">
                    {language === "pt" 
                      ? "Volte amanhã ou faça upgrade." 
                      : "Come back tomorrow or upgrade."}
                  </p>
                </div>
              ) : (
                <button
                  onClick={handleSummarize}
                  disabled={isProcessing || !file}
                  className={`neo-button neo-button-primary w-full mt-6 py-4 text-lg font-bold flex items-center justify-center gap-3 ${
                    isProcessing || !file ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  data-testid="summarize-button"
                >
                  {isProcessing ? (
                    <>
                      <SpinnerGap size={24} weight="bold" className="animate-spin" />
                      {processingStep || t.processing}
                    </>
                  ) : (
                    <>
                      <Article size={24} weight="bold" />
                      {t.summarize}
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Result Area */}
            <div 
              className={`neo-card p-6 ${isProcessing ? "loading-border" : ""}`}
              data-testid="result-section"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="heading text-lg font-bold">{t.resultTitle}</h3>
                
                {currentSummary && (
                  <div className="flex items-center gap-2">
                    <span className={`neo-badge px-3 py-1 ${typeColors[currentSummary.summary_type]}`}>
                      {t.types[currentSummary.summary_type]?.title || currentSummary.summary_type}
                    </span>
                    
                    <button
                      onClick={handleCopy}
                      className="neo-button p-2"
                      data-testid="copy-button"
                    >
                      {copied ? <Check size={18} weight="bold" /> : <Copy size={18} weight="bold" />}
                    </button>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="neo-button p-2" data-testid="download-dropdown">
                          <Download size={18} weight="bold" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="border-2 border-black bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                        <DropdownMenuItem 
                          onClick={() => handleDownload(currentSummary.id, "pdf")}
                          className="cursor-pointer font-medium"
                          data-testid="download-pdf"
                        >
                          {t.downloadPDF}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDownload(currentSummary.id, "txt")}
                          className="cursor-pointer font-medium"
                          data-testid="download-txt"
                        >
                          {t.downloadTXT}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    <button
                      onClick={() => handleDelete(currentSummary.id)}
                      className="neo-button neo-button-accent p-2"
                      data-testid="delete-button"
                    >
                      <Trash size={18} weight="bold" />
                    </button>
                  </div>
                )}
              </div>
              
              <ScrollArea className="h-[300px] md:h-[400px]">
                {currentSummary ? (
                  <div className="pr-4">
                    <p className="text-sm text-gray-600 mb-3">
                      <strong>{currentSummary.filename}</strong> • {formatDate(currentSummary.created_at)}
                    </p>
                    <div className="prose prose-sm max-w-none whitespace-pre-wrap" data-testid="summary-content">
                      {currentSummary.summary_text}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <Article size={48} weight="light" className="mb-3" />
                    <p>{t.resultEmpty}</p>
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t-2 border-black bg-white p-4 mt-8">
        <p className="text-center text-sm text-gray-600">{t.poweredBy}</p>
      </footer>
    </div>
  );
}

export default App;
